import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../../services/state/auth-state.jsx';

import Loading from '../../../feedback/loading.jsx';
import {
  AlertDialogCancel,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../ui/alert-dialog.jsx';
import { Button } from '../../../ui/button.jsx';
import { Checkbox } from '../../../ui/checkbox.jsx';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../ui/form.jsx';
import { Input } from '../../../ui/input.jsx';
import PortalHeading from '../heading.jsx';

export const FormCustomerActionNewEmail = () => {
  const { logout } = useSnapshot(authState);

  const formCustomerActionNewEmail = useForm({
    mode: 'onChange',
    defaultValues: {
      email: undefined,
      emailConfirmation: undefined,
      consentTermsOfParticipation: false,
      consentDataSharing: false,
    },
    resolver: yupResolver(
      yup.object().shape({
        email: yup.string().email().required('E-mail é um campo obrigatório'),
        emailConfirmation: yup
          .string()
          .email()
          .required('Email é um campo obrigatório'),
        consentTermsOfParticipation: yup
          .boolean(true)
          .required('Condições de participação é um campo obrigatório')
          .oneOf([true], 'Condições de participação necessita de ser aceite'),
      }),
    ),
  });

  const mutationUpdateCustomerAccountEmail = useMutation({
    mutationFn: async (data) => {
      await msCatalystApi.get(`/users?email=${data.email}`);

      await msCatalystApi.post(`/auth/update-email`, {
        email: data.email,
        isEmailVerified: false,
        loginRememberMe: false,
      });

      return true;
    },
    onSuccess: () => {
      setTimeout(async () => {
        await logout();
      }, 5000);
    },
    onError: (err) => {
      const message = err?.response?.data?.message?.toLocaleLowerCase();

      if (message.includes('duplicate value for email')) {
        toast.error('Já existe uma conta com o E-mail inserido', {
          description: 'Por favor tente novamente com um email diferente',
        });
      } else {
        toast.error(
          'Ocorreu um erro ao pedir a mudança do e-mail da sua conta',
          {
            description: 'Por favor tente novamente mais tarde',
          },
        );
      }
    },
  });

  return mutationUpdateCustomerAccountEmail.isPending ? (
    <div className="flex w-full justify-center">
      <Loading message="A alterar o e-mail da sua conta" />
    </div>
  ) : mutationUpdateCustomerAccountEmail.isSuccess ? (
    <div className="flex flex-col items-center">
      <CheckIcon className="size-40 text-success" />
      <PortalHeading className="text-center !text-2xl">
        E-mail da conta alterado com sucesso.
      </PortalHeading>

      <div className="py-4 text-center">
        <p>
          A sua sessão será terminada e terá de entrar novamente na sua conta
          com o seu novo e-mail após confirmar o mesmo via e-mail.
        </p>
      </div>
    </div>
  ) : (
    <Form key="update-email" {...formCustomerActionNewEmail}>
      <form
        name="update-email"
        onSubmit={formCustomerActionNewEmail.handleSubmit((data) => {
          mutationUpdateCustomerAccountEmail.mutate(data);
        })}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Editar E-mail</AlertDialogTitle>
        </AlertDialogHeader>

        <div className="flex flex-col items-start space-x-2">
          <div className="grid gap-x-10 gap-y-4">
            <FormField
              control={formCustomerActionNewEmail.control}
              name="email"
              render={({ field }) => (
                <FormItem variant="portal">
                  <FormLabel required={true}>E-mail</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={formCustomerActionNewEmail.control}
              name="emailConfirmation"
              render={({ field }) => (
                <FormItem variant="portal">
                  <FormLabel required={true}>Confirmar E-mail</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-4 py-4">
              <FormField
                control={formCustomerActionNewEmail.control}
                name="consentTermsOfParticipation"
                render={({ field }) => (
                  <FormItem variant="portal" className="col-span-1">
                    <FormLabel
                      className="!font-lively !text-base font-bold"
                      required={true}
                    >
                      Condições de participação
                    </FormLabel>
                    <div className="flex min-h-10 flex-row items-center space-x-2.5 pt-1 md:h-10">
                      <FormControl>
                        <Checkbox
                          className="size-6"
                          id="consentTermsOfParticipation"
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked)}
                        />
                      </FormControl>
                      <span className="text-xs text-portal-text">
                        Sim, autorizo que os dados acima sejam utilizados pelo
                        MAR SHOPPING para efeitos da minha participação em
                        campanhas realizadas pelo MAR SHOPPING.
                      </span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={formCustomerActionNewEmail.control}
                name="consentDataSharing"
                render={({ field }) => (
                  <FormItem variant="portal" className="col-span-1">
                    <FormLabel className="!font-lively !text-base font-bold">
                      Comunicações de Marketing
                    </FormLabel>
                    <div className="flex min-h-10 flex-row items-center space-x-2.5 pt-1 md:h-10">
                      <FormControl>
                        <Checkbox
                          className="size-6"
                          id="consentDataSharing"
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked)}
                        />
                      </FormControl>
                      <span className="text-xs text-portal-text">
                        Sim, autorizo que os dados acima sejam utilizados pelo
                        MAR SHOPPING e seus parceiros no âmbito de ações
                        informativas e comerciais.
                      </span>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={!formCustomerActionNewEmail.formState.isDirty}
            onClick={() => formCustomerActionNewEmail.reset()}
          >
            Cancelar
          </AlertDialogCancel>

          <Button
            type="submit"
            disabled={!formCustomerActionNewEmail.formState.isDirty}
          >
            Confirmar
          </Button>
        </AlertDialogFooter>
      </form>
    </Form>
  );
};
